import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { Divider, Stack, Typography } from '@optimus-web/core';
import { TextKey, DataTestId, FeatureFlag } from '@mews-commander/enums';
import { LocalizedMinorHighlight } from '@mews-commander/ui';
import { FormSwitchField, lt, useDialog, useFeatureFlag } from '@mews/framework';
import { CompaniesFormValues, RatesOptionsNames, TargetBillType } from '../../../../../schemas';
import { useRatesSuspenseQuery, useServicesSuspenseQuery } from '../queries';
import { getSelectedCompaniesCount } from '../../../../../utils/companies';
import { RoutingRulesFormFields } from '../../../utils';
import { CustomerProfileSearchField } from '../../../../../../../components/form/hookFields/asyncSelectField/CustomerProfileSearchField/CustomerProfileSearchField';
import { RateSelectField } from './RateSelectField';
import { CompanySelectField } from './CompanySelectField';
import { GroupsAndReservationsSelectField } from './GroupsAndReservations/GroupsAndReservationsSelectField';
import { useSelectedGroupsAndReservationsCount } from './GroupsAndReservations/useSelectedGroupsAndReservationsCount';

export const ConditionsFields = () => {
    const { data: rates } = useRatesSuspenseQuery();

    const { watch, setValue, trigger } = useFormContext();
    const {
        data: { bookableServices },
    } = useServicesSuspenseQuery();
    const [{ onClose, active }, openDialog] = useDialog();
    const [{ onClose: onCloseReservationsDialog, active: activeReservationsDialog }, openReservationsDialog] =
        useDialog();

    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );

    const enabledBookableServices = bookableServices.filter((bookableService) =>
        watch(RoutingRulesFormFields.serviceEnabled(bookableService.id))
    );

    const areRatesEnabled = watch(RoutingRulesFormFields.ratesEnabled());
    const areCompaniesEnabled = watch(RoutingRulesFormFields.companiesEnabled());
    const areGroupsAndReservationsEnabled = watch(RoutingRulesFormFields.groupsAndReservationsEnabled());
    const areGuestProfilesEnabled = watch(RoutingRulesFormFields.guestProfilesEnabled());
    const selectedCompanies: CompaniesFormValues = watch(RoutingRulesFormFields.companies());

    const selectedGroupsAndReservationsCount = useSelectedGroupsAndReservationsCount();

    useEffect(() => {
        if (!areCompaniesEnabled) {
            setValue(RoutingRulesFormFields.targetType(), TargetBillType.GuestBill);
        }
    }, [areCompaniesEnabled, setValue]);

    const guestProfiles = watch(RoutingRulesFormFields.guestProfiles());
    const guestProfilesEnabled = watch(RoutingRulesFormFields.guestProfilesEnabled());
    const guestProfileTarget = watch(RoutingRulesFormFields.guestProfileTarget());

    useEffect(() => {
        if (!granularRoutingRulesConditionsExtensionEnabled) return;

        if (!guestProfilesEnabled || guestProfiles.length === 0) {
            setValue(RoutingRulesFormFields.guestProfileTarget(), null);
        } else if (guestProfiles.length === 1 && !guestProfileTarget) {
            // Prefill guestProfileTarget with the first profile if not set
            setValue(RoutingRulesFormFields.guestProfileTarget(), guestProfiles[0]);
        }
    }, [
        guestProfiles,
        setValue,
        granularRoutingRulesConditionsExtensionEnabled,
        guestProfileTarget,
        guestProfilesEnabled,
    ]);

    return (
        <Stack vertical distribution="stretch" spacing="200">
            <FormSwitchField
                name={RoutingRulesFormFields.companiesEnabled()}
                label={<LocalizedMinorHighlight textKey={TextKey.RoutingRulesFormCompaniesOrTravelAgenciesLabel} />}
                onChange={(_, value) => {
                    if (value && getSelectedCompaniesCount(selectedCompanies) === 0) {
                        openDialog();
                    }
                }}
            />
            {areCompaniesEnabled && <CompanySelectField onClose={onClose} active={active} openDialog={openDialog} />}
            <Divider />
            <FormSwitchField
                name={RoutingRulesFormFields.ratesEnabled()}
                onChange={(_, value) => {
                    if (!value) {
                        enabledBookableServices.forEach((service) => {
                            setValue(RoutingRulesFormFields.serviceRatesType(service.id), RatesOptionsNames.All);
                        });
                        void trigger();
                    }
                }}
                label={<LocalizedMinorHighlight textKey={TextKey.RatesPlural} />}
            />
            {areRatesEnabled && enabledBookableServices.length === 0 && (
                <Typography variant="secondary" textStyle="bodyMedium" as="div">
                    {lt('RoutingRulesFormFieldDescriptionNoSelectedService')}
                </Typography>
            )}
            {areRatesEnabled &&
                enabledBookableServices.map((bookableService) => (
                    <RateSelectField
                        key={bookableService.id}
                        service={bookableService}
                        rates={rates}
                        dataTestId={`${DataTestId.RoutingRulesRatesField}-${bookableService.name}`}
                    />
                ))}
            <Divider />
            <FormSwitchField
                name={RoutingRulesFormFields.prepaidReservationsEnabled()}
                label={<LocalizedMinorHighlight textKey={TextKey.RoutingRulesFormPrepaidReservationsLabel} />}
            />
            {granularRoutingRulesConditionsExtensionEnabled && (
                <>
                    <Divider />
                    <FormSwitchField
                        name={RoutingRulesFormFields.groupsAndReservationsEnabled()}
                        label={<LocalizedMinorHighlight textKey={TextKey.RoutingRulesFormGroupsAndReservationsLabel} />}
                        onChange={(_, value) => {
                            if (value && selectedGroupsAndReservationsCount === 0) {
                                openReservationsDialog();
                            }
                        }}
                    />
                    {areGroupsAndReservationsEnabled && (
                        <GroupsAndReservationsSelectField
                            onClose={onCloseReservationsDialog}
                            active={activeReservationsDialog}
                            openDialog={openReservationsDialog}
                        />
                    )}
                    <Divider />
                    <FormSwitchField
                        name={RoutingRulesFormFields.guestProfilesEnabled()}
                        label={<LocalizedMinorHighlight textKey={TextKey.RoutingRulesFormGuestProfilesLabel} />}
                    />
                    {areGuestProfilesEnabled && (
                        <CustomerProfileSearchField
                            required
                            label={lt('RoutingRulesFormGuestProfilesLabel')}
                            name={RoutingRulesFormFields.guestProfiles()}
                            placeholder={lt('RoutingRulesFormGuestProfilesSearchPlaceholder')}
                            newCustomerButtonText={lt('RoutingRulesFormGuestProfilesAddNewGuestProfile')}
                            initialEmptyMessage={
                                <Typography textStyle="bodyMediumStrong" variant="tertiary" as="div">
                                    {lt('RoutingRulesFormGuestProfilesSearchEmptyMessage')}
                                </Typography>
                            }
                            multiselect
                        />
                    )}
                </>
            )}
        </Stack>
    );
};
